<script setup>
import {ref, onMounted} from 'vue';
import OrderBadgeModal from '@/components/resources/order-badges/components/OrderBadgeModal.vue';
import $http from '@/axios.config'
import {log} from "@/utils/clientLogger.js";
import {useI18n} from "vue-i18n";
import {showToast} from "@/utils/swiperToast";
import helpers from "@root/helpers.js";

const badges = ref([]);
const loading = ref(false);
const error = ref(null);
const isModalOpen = ref(false);
const selectedBadge = ref(null);
const badgesPagination = ref({page: 1, perPage: 20, totalItems: 0, name: ''})
const {t, locale} = useI18n();

const getBadges = async () => {
  loading.value = true;
  error.value = null;
  try {
    $http.get('/api/badges', {
      params: {
        limit: badgesPagination.value.perPage,
        page: badgesPagination.value.page
      }
    }).then(res => {
      badges.value = res.data
      badgesPagination.value.totalItems = res.data.total
    }).catch(error => {
      log.error('View:AdminPage:getBadges', {'error': error})
    })
  } catch (err) {
    await showToast(t('common.load_error', {name: t('labels.badge')}),'error')
    error.value = t('common.load_error', {name: t('labels.badge')});
    log.error('View:OrderBadgeIndex:getBadges', {'error': err});
  } finally {
    loading.value = false;
  }
};

const openCreateModal = () => {
  selectedBadge.value = null;
  isModalOpen.value = true;
};

const openEditModal = (badge) => {
  selectedBadge.value = {...badge};
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
  selectedBadge.value = null;
};

const handleSubmit = async ({data, isEdit}) => {
  try {
    if (isEdit && selectedBadge.value?._id) {
      await $http.put(`/api/badges/${selectedBadge.value._id}`, data);
    } else {
      await $http.post('/api/badges', data);
    }
    await getBadges();
    closeModal();
    const successMessage = isEdit
        ? t('common.updated_success', {name: t('labels.badge')})
        : t('common.created_success', {name: t('labels.badge')});
    await showToast(successMessage,'info')
  } catch (err) {
    error.value = isEdit
        ? t('common.updated_error', {name: t('labels.badge')})
        : t('common.created_error', {name: t('labels.badge')});
    await showToast(error.value,'error')
    log.error('View:OrderBadgeIndex:handleSubmit', {'error': err});
  }
};

const confirmDelete = async (badge) => {
  if (confirm(t('common.confirm_delete', {name: badge.name}))) {
    await deleteBadge(badge._id);
  }
};

const deleteBadge = async (id) => {
  try {
    await $http.delete(`/api/badges/${id}`);
    await getBadges();
    const successMessage = t('common.deleted_success', {name: t('labels.badge')});
    await showToast(successMessage,'info')
  } catch (err) {
    error.value = t('common.deleted_error', {name: t('labels.badge')});
    await showToast(error.value,'error')
    log.error('View:OrderBadgeIndex:deleteBadge', {'error': err});
  }
};

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('ru-RU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

onMounted(() => {
  getBadges();
});
</script>

<template>
  <div class="bg-body-secondary rounded-4 p-3 pb-0 mt-3">
    <button class="btn btn-dark w-100 mb-3" @click="openCreateModal">
      <i class="bi bi-patch-plus"></i> {{ t('buttons.add') }}
    </button>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Загрузка...</span>
      </div>
    </div>

    <div v-else-if="error" class="alert alert-danger" role="alert">
      {{ error }}
    </div>

    <div v-else class="table-responsive">
      <table class="table table-striped table-hover table-bordered align-baseline">
        <thead>
        <tr>
          <th>{{ t('tableheadings.title') }}</th>
          <th>{{ t('tableheadings.type') }}</th>
          <th>{{ t('tableheadings.preview') }}</th>
          <th>{{ t('tableheadings.created') }}</th>
          <th class="text-end">{{ t('tableheadings.table_actions') }}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="badge in badges" :key="badge._id">
          <td>{{ badge.name }}</td>
          <td>{{ badge.type }}</td>
          <td>
            <div class="badge" :class="`bg-${badge.type}`">
              {{ helpers.getBadgeName(badge, locale) }}
            </div>
          </td>
          <td>{{ formatDate(badge.createdAt) }}</td>
          <td class="text-end">
            <div class="btn-group w-100" role="group">
              <button
                  class="btn btn-sm btn-warning"
                  @click="openEditModal(badge)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-pencil-square" viewBox="0 0 16 16">
                  <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                  <path fill-rule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"/>
                </svg>
              </button>
              <button
                  class="btn btn-sm btn-danger"
                  @click="confirmDelete(badge)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-trash" viewBox="0 0 16 16">
                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z"/>
                  <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z"/>
                </svg>
              </button>
            </div>
          </td>
        </tr>
        <tr v-if="badges.length === 0">
          <td colspan="5" class="text-center py-5 fw-bold text-muted">
            {{ t('table.placeholders.empty_heading') }}
            <br>
            {{ t('table.placeholders.empty_description') }}
          </td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>

  <OrderBadgeModal
      :is-open="isModalOpen"
      :badge="selectedBadge"
      @close="closeModal"
      @submit="handleSubmit"
  />
</template>

<style scoped>

</style>